import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { apiClient, AssistantChatMessageFeedback, AuthorRole } from 'lib/apiClient'
import { QueryKey } from 'lib/queryClient'

import { AssistantChatMessagesContextState } from '../providers'

interface IAssistantChatMessagesFeedbackHook {
  chatMessagesFeedback: AssistantChatMessageFeedback[]
  isLoading: boolean
  isError: boolean
  retry: () => void
  handleOnChatMessageFeedbackVisible: (id: string) => void
  refetchChatMessageFeedbackById: (
    id: string,
  ) => Promise<AssistantChatMessageFeedback | null>
}

interface IAssistantChatMessagesFeedbackHookProps
  extends Pick<
    AssistantChatMessagesContextState,
    | 'chatMessages'
    | 'chatMessagesFeedbackMap'
    | 'setChatMessagesFeedbackMap'
    | 'getChatMessageFeedbackById'
    | 'removeAssistantChatMessageFeedbackById'
  > {}

const limit = 100

export function useAssistantChatMessagesFeedback({
  chatMessages,
  chatMessagesFeedbackMap,
  setChatMessagesFeedbackMap,
  removeAssistantChatMessageFeedbackById,
}: IAssistantChatMessagesFeedbackHookProps): IAssistantChatMessagesFeedbackHook {
  // Initial loading only
  const isLoading = useRef(true)
  const [offset, setOffset] = useState<number>(0)

  // Create batch ids array with only agent chats, reversed, paginated
  const feedbackMessageIds = useMemo(
    () =>
      chatMessages
        .filter(message => message.authorRole === AuthorRole.Agent) // filter only agent messages
        .slice() // make a copy (reverse mutates object)
        .reverse() // reverse copy
        .slice(offset * limit, offset * limit + limit) // paginate
        .map(message => message.messageId),
    [chatMessages, offset],
  )

  const { data, isError, refetch } = useQuery({
    queryKey: [QueryKey.messagesFeedback, feedbackMessageIds],
    queryFn: async () => {
      const response = await apiClient.getAssistantMessagesFeedback(feedbackMessageIds)

      // cache all searched + fetched feedbacks
      const newResults = new Map<string, AssistantChatMessageFeedback>(
        response.feedbacks.map(feedback => [feedback.assistantGenerationId, feedback]),
      )
      setChatMessagesFeedbackMap(new Map([...chatMessagesFeedbackMap, ...newResults]))

      if (isLoading.current) {
        isLoading.current = false
      }

      return response
    },
    enabled: Boolean(feedbackMessageIds.length),
    retry: false,
  })

  // Fetching new batch when top half message is on screen
  const handleOnChatMessageFeedbackVisible = (id: string) => {
    if (
      feedbackMessageIds.findIndex(messageId => messageId === id) >=
      Math.floor(feedbackMessageIds.length / 2)
    ) {
      setOffset(prev => prev + 1)
    }
  }

  const refetchChatMessageFeedbackById = async (
    id: string,
  ): Promise<AssistantChatMessageFeedback | null> => {
    try {
      const response = await apiClient.getAssistantMessagesFeedback([id])
      removeAssistantChatMessageFeedbackById(id)

      // cache all searched + fetched feedbacks
      const newResults = new Map<string, AssistantChatMessageFeedback>(
        response.feedbacks.map(feedback => [feedback.assistantGenerationId, feedback]),
      )

      setChatMessagesFeedbackMap(new Map([...chatMessagesFeedbackMap, ...newResults]))
      return response.feedbacks[0]
    } catch (error) {
      console.error(error)
      return null
    }
  }

  return {
    chatMessagesFeedback: data?.feedbacks ?? [],
    isError,
    isLoading: isLoading.current,
    retry: refetch,
    refetchChatMessageFeedbackById,
    handleOnChatMessageFeedbackVisible,
  }
}
